import type { SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import { toUzPhoneE164 } from "@/shared/utils/uz-phone";

export type AdminRole = {
	description?: string;
	id: string;
	name: string;
};

export type AdminUser = {
	createdAt: string;
	email: string;
	fullName: string;
	id: string;
	isActive: boolean;
	lastLoginAt: null | string;
	orgId: null | string;
	role: null | string;
	updatedAt: string;
	username: string;
	phone: string;
};

export type AdminUserCreatePayload = {
	email: string;
	fullName: string;
	isActive?: boolean;
	orgId?: string;
	password: string;
	roleId?: string;
	username: string;
	phone: string;
};

export type UsersPageRole = "admin" | "manager" | "super";

const SYSTEM_USER_ROLE_IDS = new Set(["super_admin", "org_admin", "sales_manager"]);

export function normalizeUserPhone(value: string): string | null {
	return toUzPhoneE164(value);
}

export function isManagedUserVisible(
	currentRole: UsersPageRole,
	currentUserId: string | undefined,
	user: Pick<AdminUser, "id" | "role">
) {
	if (currentUserId && user.id === currentUserId) {
		return false;
	}

	if (currentRole === "super") {
		return user.role !== "super";
	}

	if (currentRole === "admin") {
		return user.role === "manager";
	}

	return false;
}

function toBackendUserRole(roleId?: string) {
	switch (roleId) {
		case "admin":
		case "ORG_ADMIN":
		case "org_admin":
			return "org_admin";
		case "manager":
		case "SALES_MANAGER":
		case "sales_manager":
			return "sales_manager";
		default:
			throw new Error("Foydalanuvchi uchun noto'g'ri rol tanlandi.");
	}
}

/** Raw user shape returned by the backend API */
export interface BackendUserData {
	id: string;
	orgId?: string | null;
	fullName?: string;
	firstName?: string;
	lastName?: string;
	username?: string;
	email?: string;
	phone?: string;
	isActive?: boolean;
	lastLoginAt?: string | null;
	role?: string;
	createdAt?: string;
	updatedAt?: string;
}

function mapBackendUser(data: BackendUserData): AdminUser {
	const roleMap = new Map<string, string>([
		["super_admin", "super"],
		["org_admin", "admin"],
		["sales_manager", "manager"],
		["SUPER_ADMIN", "super"],
		["ORG_ADMIN", "admin"],
		["SALES_MANAGER", "manager"],
	]);

	return {
		id: data.id,
		orgId: data.orgId ?? null,
		fullName:
			data.fullName ||
			`${data.firstName || ""} ${data.lastName || ""}`.trim() ||
			data.username ||
			"Foydalanuvchi",
		username: data.username || "",
		email: data.email || "",
		phone: data.phone || "",
		isActive: data.isActive ?? true,
		lastLoginAt: data.lastLoginAt ?? null,
		role: (data.role ? roleMap.get(data.role) : undefined) || data.role || null,
		createdAt: data.createdAt ?? "",
		updatedAt: data.updatedAt ?? "",
	};
}

export async function getAdminUsers(params: { orgId?: string } = {}) {
	const response = await apiClient.get<
		SuccessResponse<{
			items: BackendUserData[];
			meta: { total: number; page: number; limit: number; totalPages: number };
		}>
	>(endpoints.users.admin, {
		params: { isActive: true },
	});

	const allItems = response.data.data.items.map(mapBackendUser);
	const items = params.orgId
		? allItems.filter((user: AdminUser) => user.orgId === params.orgId)
		: allItems;
	const total = params.orgId ? items.length : response.data.data.meta.total;
	const limit = response.data.data.meta.limit;
	const page = response.data.data.meta.page;
	const totalPages = params.orgId ? Math.ceil(total / limit) : response.data.data.meta.totalPages;

	return {
		items,
		meta: {
			total,
			page,
			limit,
			totalPages,
		},
	};
}

export async function getAdminUserById(userId: string) {
	const response = await apiClient.get<SuccessResponse<BackendUserData>>(
		endpoints.users.detail(userId)
	);
	return mapBackendUser(response.data.data);
}

type RoleApiItem = {
	id: string;
	name: string;
	description?: string;
};

export async function getAdminRoles() {
	const response = await apiClient.get<SuccessResponse<{ items: RoleApiItem[] } | RoleApiItem[]>>(
		endpoints.roles.list
	);
	const items = Array.isArray(response.data.data) ? response.data.data : response.data.data.items;
	return items
		.filter((item: RoleApiItem) => SYSTEM_USER_ROLE_IDS.has(item.id))
		.map((item: RoleApiItem) => ({
			id: item.id,
			name: item.name,
			description: item.description,
		}));
}

export async function submitUserCreate(payload: AdminUserCreatePayload) {
	const parts = payload.fullName.trim().split(/\s+/);
	const firstName = parts[0] || "";
	const lastName = parts.slice(1).join(" ") || "";

	const backendPayload = {
		firstName,
		lastName,
		username: payload.username,
		email: payload.email,
		phone: payload.phone,
		password: payload.password,
		role: toBackendUserRole(payload.roleId),
		isActive: payload.isActive ?? true,
	};

	const response = payload.orgId
		? await apiClient.post<SuccessResponse<BackendUserData>>(
				endpoints.users.create,
				backendPayload,
				{
					params: { orgId: payload.orgId },
				}
			)
		: await apiClient.post<SuccessResponse<BackendUserData>>(
				endpoints.users.create,
				backendPayload
			);

	return mapBackendUser(response.data.data);
}

export type AdminUserUpdatePayload = {
	email?: string;
	fullName?: string;
	isActive?: boolean;
	password?: string;
	roleId?: string;
	username?: string;
	phone?: string;
};

export async function submitUserUpdate(userId: string, payload: AdminUserUpdatePayload) {
	const updateData: Record<string, unknown> = {};

	if (payload.fullName !== undefined) {
		const parts = payload.fullName.trim().split(/\s+/);
		updateData.firstName = parts[0] || "";
		updateData.lastName = parts.slice(1).join(" ") || "";
	}
	if (payload.username !== undefined) {
		updateData.username = payload.username;
	}
	if (payload.email !== undefined) {
		updateData.email = payload.email;
	}
	if (payload.phone !== undefined) {
		updateData.phone = payload.phone;
	}
	if (payload.password !== undefined) {
		updateData.password = payload.password;
	}
	if (payload.isActive !== undefined) {
		updateData.isActive = payload.isActive;
	}

	if (payload.roleId !== undefined) {
		updateData.role = toBackendUserRole(payload.roleId);
	}

	const response = await apiClient.patch<SuccessResponse<BackendUserData>>(
		endpoints.users.update(userId),
		updateData
	);

	return mapBackendUser(response.data.data);
}

export async function submitUserDelete(userId: string) {
	await apiClient.delete<void>(endpoints.users.update(userId));
}
